/**
 * billing 도메인 TanStack Query 키.
 *
 * web 과 console-web 이 같은 키를 쓴다. 키가 갈라지면 같은 데이터가 앱마다 다른
 * 캐시에 앉아 무효화 범위를 추적할 수 없게 된다.
 */
export const businessKeys = {
  all: ['business'] as const,
  myGroups: ['business', 'myGroups'] as const,
  group: (orgId: string) => ['business', 'group', orgId] as const,
  members: (orgId: string) => ['business', 'members', orgId] as const,
  ownershipTransfer: (orgId: string) =>
    ['business', 'ownershipTransfer', orgId] as const,
  seats: (orgId: string) => ['business', 'seats', orgId] as const,
  seatLicenses: (orgId: string) => ['business', 'seatLicenses', orgId] as const,
  ownedPlans: (orgId: string) => ['business', 'ownedPlans', orgId] as const,
  billingSubs: (orgId: string) => ['business', 'billingSubs', orgId] as const,
  orgSettlements: (orgId: string) =>
    ['business', 'orgSettlements', orgId] as const,
  /** 정산 1건 — 결제 결과 확인 전용. 배너 캐시(orgSettlements)와 섞이면 안 된다. */
  orgSettlement: (orgId: string, cancelRequestId: string) =>
    ['business', 'orgSettlement', orgId, cancelRequestId] as const,
  seatsPreview: (
    orgId: string,
    planId: string,
    count: number,
    subscriptionId?: string,
  ) =>
    ['business', 'seatsPreview', orgId, planId, count, subscriptionId] as const,
  assignableMembers: (orgId: string) =>
    ['business', 'assignableMembers', orgId] as const,
  licenseSummary: (orgId: string) =>
    ['business', 'licenseSummary', orgId] as const,
  memberLicenses: (orgId: string, userId: string) =>
    ['business', 'memberLicenses', orgId, userId] as const,
  orgPayments: (orgId: string, page: number) =>
    ['business', 'orgPayments', orgId, page] as const,
  orgPaymentDetail: (orgId: string, id: string) =>
    ['business', 'orgPaymentDetail', orgId, id] as const,
  verifications: (orgId: string) => ['business', 'verifications', orgId] as const,
  invitations: (orgId: string) => ['business', 'invitations', orgId] as const,
  myJoinRequests: ['business', 'myJoinRequests'] as const,
};

/**
 * 디바이스락 쿼리 키. 해제 후엔 목록과 교체 횟수가 함께 바뀌므로 범위째 무효화한다.
 */
export const licenseDeviceKeys = {
  all: ['licenseDevice'] as const,
  mine: ['licenseDevice', 'me'] as const,
  org: (orgId: string) => ['licenseDevice', 'org', orgId] as const,
};

/** 구독·라이선스 조회 쿼리 키. */
export const subscriptionKeys = {
  all: ['subscription'] as const,
  myActive: ['subscription', 'me', 'active'] as const,
  myActiveLicenses: ['subscription', 'me', 'licenses', 'active'] as const,
  myOwnedProducts: ['subscription', 'me', 'ownedProducts'] as const,
  myLegacyTerms: ['subscription', 'me', 'legacyTerms'] as const,
  /** 내 사용권. 구독 변화로만 바뀌므로 `all` 아래에 둬 구독 무효화에 같이 걸리게 한다. */
  myEntitlement: ['subscription', 'me', 'entitlement'] as const,
  /** 청약철회 프리뷰(§6.1) — 구독별. 7일 창 안에서만 조회한다. */
  withdrawalPreview: (id: string) =>
    ['subscription', id, 'withdrawal', 'preview'] as const,
  /** 개인 연간 중도해지 환불 프리뷰 — 구독별. */
  annualTerminationPreview: (id: string) =>
    ['subscription', id, 'annual-termination', 'preview'] as const,
  /** 재활성화 프리뷰(§6.3) — 청구 예정 금액. */
  reactivatePreview: (id: string) =>
    ['subscription', id, 'reactivate', 'preview'] as const,
};

/**
 * 교육 도메인 쿼리 키.
 * 접수(mine)와 카탈로그(courses/sessions)를 갈라 둔다 — 접수 후 무효화가
 * 카탈로그 잔여석까지 덮으면 목록이 매번 다시 뜬다.
 */
export const educationKeys = {
  all: ['education'] as const,
  courses: (family?: string) => ['education', 'courses', family ?? ''] as const,
  course: (id: string) => ['education', 'course', id] as const,
  sessions: (filter: string) => ['education', 'sessions', filter] as const,
  session: (id: string) => ['education', 'session', id] as const,
  memberOptions: (sessionIds: string[]) =>
    ['education', 'memberOptions', ...sessionIds] as const,
  myRegistrations: (page: number) =>
    ['education', 'myRegistrations', page] as const,
  myRegistration: (id: string) =>
    ['education', 'myRegistration', id] as const,
};
